// Мост к оболочке десктопа (ADR-014: `DesktopShell`).
//
// Веб-код не форкается: он спрашивает, есть ли оболочка, и если есть — берёт у неё сеть, секреты,
// локальную базу и уведомления. Если оболочки нет, всё работает ровно как раньше, в браузере. Так
// замена Electron на что-то другое останется заменой этого слоя, а не переписыванием клиента.

/** Состояние хранилища секретов системы. */
export interface ShellSecretsStatus {
  encryption: boolean;
  backend: string;
  warning: string;
}

/** Сведения о сборке оболочки. */
export interface ShellInfo {
  version: string;
  electron: string;
  platform: string;
  node: string;
  secrets: ShellSecretsStatus;
}

/** То, что оболочка кладёт в окно. Ничего из Electron сюда не попадает — только функции и данные. */
export interface DesktopShell {
  platform: string;
  net: {
    setNode(origin: string): Promise<string>;
    open(request: {
      id: string;
      url: string;
      method: string;
      headers: Record<string, string>;
      body?: Uint8Array;
    }): Promise<{ status: number; statusText: string; headers: Record<string, string> }>;
    onChunk(id: string, cb: (chunk: Uint8Array) => void): () => void;
    onEnd(id: string, cb: () => void): () => void;
    onError(id: string, cb: (message: string) => void): () => void;
    cancel(id: string): void;
  };
  secrets: {
    status(): Promise<ShellSecretsStatus>;
    get(key: string): Promise<string | undefined>;
    set(key: string, value: string): Promise<void>;
    delete(key: string): Promise<void>;
  };
  database: {
    open(name: string): Promise<{ path: string }>;
    /** Синхронно: интерфейс базы в ядре синхронный, а запрос идёт к локальному файлу. */
    run(sql: string, params: unknown[]): { ok: boolean; error?: string };
    all(sql: string, params: unknown[]): { ok: boolean; error?: string; rows?: Record<string, unknown>[] };
    status(): Promise<{ open: boolean; path: string; encrypted: boolean }>;
  };
  notifications: {
    show(options: { title: string; body: string; chatId: string }): Promise<boolean>;
    /** Число и согласованная с ним форма слова: «1 непрочитанное», «5 непрочитанных». */
    setBadge(count: number, text: string): Promise<boolean>;
    onActivate(cb: (chatId: string) => void): () => void;
    /** Звук отключён из трея до этого времени (мс); 0 — включён. */
    onMuted(cb: (until: number) => void): () => void;
  };
  /** Оболочка просит открыть раздел клиента: «settings», «notifications», «about». */
  onOpen(cb: (section: string) => void): () => void;
  /** Автозапуск при входе в систему: без аргумента — прочитать, с аргументом — задать. */
  autostart(value?: boolean): Promise<boolean>;
  info(): Promise<ShellInfo>;
  /** Запись в журнал приложения: администратор читает его без отладчика. */
  log(level: "info" | "warn" | "error", message: string): void;
}

declare global {
  interface Window {
    yofisShell?: DesktopShell;
  }
}

/** Оболочка, если клиент запущен на десктопе. */
export function desktopShell(): DesktopShell | undefined {
  return window.yofisShell;
}

/**
 * Сеть через оболочку: рендерер сам в сеть не ходит. Ответ собирается кусками — у стрима событий тело
 * не кончается, и ждать его целиком нельзя.
 */
export function shellFetch(shell: DesktopShell): typeof fetch {
  return async (input, init) => {
    const request = new Request(input as RequestInfo, init);
    const id = crypto.randomUUID();
    const headers: Record<string, string> = {};
    request.headers.forEach((value, name) => {
      headers[name] = value;
    });
    const body = request.method === "GET" || request.method === "HEAD"
      ? undefined
      : new Uint8Array(await request.clone().arrayBuffer());

    const head = await shell.net.open({
      id,
      url: request.url,
      method: request.method,
      headers,
      ...(body === undefined ? {} : { body }),
    });

    const stream = new ReadableStream<Uint8Array>({
      start(controller) {
        const offChunk = shell.net.onChunk(id, (chunk) => controller.enqueue(new Uint8Array(chunk)));
        shell.net.onEnd(id, () => {
          offChunk();
          controller.close();
        });
        shell.net.onError(id, (message) => {
          offChunk();
          controller.error(new Error(message));
        });
      },
      cancel() {
        shell.net.cancel(id);
      },
    });

    return new Response(stream, { status: head.status, statusText: head.statusText, headers: head.headers });
  };
}
